import { EngineStore } from '@/store/Engine/EngineStore';
import { t } from 'i18next';
import FlowIconOutlined from '@/comp/common/icons/FlowIconOutlined';
import LightningIcon from '@/comp/common/icons/LightningIcon';
import React from 'react';
import DevicesStore from '@/store/Devices/DevicesStore';

export const FLOW_TYPE = 'flow';
export const PRESSURE_TYPE = 'pressure';

export interface SensorsKPITabProps {
  setIsSaveDisabled?: (value: boolean) => void;
  hasTriedToSubmit?: boolean;
  activeTab?: string;
  isPressureFallbackAllowed?: boolean;
  isFlowFallbackAllowed?: boolean;
}

export interface KPISensorCardProps {
  title: string;
  sensorsPriority;
  icon: React.ReactNode;
  type: string;
  sensorsOptions: Array<{ label: string; value: string }>;
  setIsSaveDisabled?: (value: boolean) => void;
  hasTriedToSubmit?: boolean;
  isPressureFallbackAllowed?: boolean;
  isFlowFallbackAllowed?: boolean;
  isAlwaysReadOnly?: boolean;
}

export const getSensorPriority = (sensorsConfiguration, type: string) => {
  return sensorsConfiguration?.[`${type}SensorsPriority`];
};

export const getSensorOptions = (devicesStore: DevicesStore, type: string) => {
  return devicesStore[`${type}Sensors`]?.map((device: { name: string; id: string }) => ({
    label: device?.name,
    value: device?.id,
  }));
};

export const handleSaveDisabled = (setIsSaveDisabled: (value: boolean) => void, sensorsConfiguration) => {
  if (setIsSaveDisabled && sensorsConfiguration?.hasChanges()) {
    setIsSaveDisabled(false);
  }
};

export const handleDeleteIconClick =
  (sensorsPriority, setIsSaveDisabled: (value: boolean) => void) => (sensorPriorityKey: string) => {
    sensorsPriority?.deleteSensorPriority(sensorPriorityKey);
    if (setIsSaveDisabled) setIsSaveDisabled(false);
  };

export const handleAddFallback =
  (sensorsPriority, type: string, engineStore: EngineStore, setIsSaveDisabled: (value: boolean) => void) => () => {
    sensorsPriority?.addSensorPriority(type);
    if (setIsSaveDisabled) {
      setIsSaveDisabled(true);
    }
  };

export const handleSensorPriorityFieldChange =
  (
    sensorsPriority,
    engineStore: EngineStore,
    devicesStore: DevicesStore,
    type: string,
    setIsSaveDisabled: (value: boolean) => void,
  ) =>
  (sensorPriorityKey: string, field: string) =>
  value => {
    sensorsPriority?.setSensorPriorityField(
      sensorPriorityKey,
      field,
      field === 'sensors'
        ? value?.map((itemValue: string) => ({
            id: itemValue,
            name: devicesStore.deviceMetadataFromDeviceId(itemValue)?.name,
          }))
        : value,
    );

    engineStore.validate('sensorsConfiguration', `${type}SensorsPriority`, null, `${field}-${sensorPriorityKey}`);

    if (field === 'sensors' && (!value || value.length === 0)) {
      if (setIsSaveDisabled) setIsSaveDisabled(true);
    } else if (setIsSaveDisabled && engineStore?.sensorsConfiguration?.hasChanges()) {
      setIsSaveDisabled(false);
    }
  };

export const validateOnSubmit = (hasTriedToSubmit: boolean | undefined, engineStore: EngineStore, type: string) => {
  if (hasTriedToSubmit) {
    engineStore.validate('sensorsConfiguration', `${type}SensorsPriority`);
  }
};

export const getSensorName = (devicesStore: DevicesStore, engineStore: EngineStore, sensorId: string, type: string) => {
  return (
    devicesStore.deviceMetadataFromDeviceId(sensorId)?.name ??
    engineStore.pidChanges?.[`${type}SensorsRemoved`]?.find(removedSensor => removedSensor?.deviceId === sensorId)
      ?.deviceName
  );
};

export function getSensorsTypeData(
  flowSensorsPriority,
  flowSensorsOptions,
  pressureSensorsPriority,
  pressureSensorsOptions,
) {
  return [
    {
      sensorsPriority: flowSensorsPriority,
      sensorsOptions: flowSensorsOptions,
      title: t('flowSensorKPI'),
      type: FLOW_TYPE,
      icon: React.createElement(FlowIconOutlined),
    },
    {
      sensorsPriority: pressureSensorsPriority,
      sensorsOptions: pressureSensorsOptions,
      title: t('pressureSensorKPI'),
      type: PRESSURE_TYPE,
      icon: React.createElement(LightningIcon, { size: 18 }),
    },
  ];
}
